import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { SPAN_REMOVE_BROADBAND, SPAN_SUBMIT_BROADBAND } from '@constants/otel';
import { paths } from '@constants/paths';
import {
  BroadbandUsageRequirementsInput,
  ProductType,
  Router,
} from '@generated/gql/graphql';
import { useDebouncedIsLoading } from '@hooks/useDebouncedIsLoading';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useUpdatedTariffModalContext } from '@providers/basket/UpdatedTariffModalProvider/Context';
import { useTrace } from '@services/otel';
import { BroadbandFormValues } from '@src/types/broadband';
import { BundleBasketData, UpdatedTariffModalVariant } from '@src/types/bundle';
import { BooleanSelection } from '@src/types/index';

import { getSelectedQuoteUnwrapped } from './getSelectedQuoteUnwrapped';
import {
  deselectBroadbandQuoteQuery,
  selectBroadbandQuoteQuery,
  submitBroadbandRequirementsQuery,
} from './query';
import { useBroadbandSelectTracking } from './useBrodbandSelectTracking';
import { shouldShowDigitalHomePhone } from './utils/shouldShowDigitalHomePhone';

const useBroadbandSubmitter = (basket: Nullable<BundleBasketData>) => {
  const { withTrace } = useTrace();
  const navigate = useNavigate();

  const { setModalState } = useUpdatedTariffModalContext();

  const { handleSelectedBroadbandItemsForEventTracking } =
    useBroadbandSelectTracking(basket);

  const setSnackbarError = useSnackbarError();

  const { basketId } = useBasketContext();

  const [submitBroadbandRequirements, { loading: requirementsLoading }] =
    useMutation(submitBroadbandRequirementsQuery);

  const [selectBroadbandQuote, { loading: loadingSelectQuote }] = useMutation(
    selectBroadbandQuoteQuery
  );

  const [
    deselectBroadbandQuote,
    { loading: loadingDeselectQuote, error: errorDeselectQuote },
  ] = useMutation(deselectBroadbandQuoteQuery);

  const isLoading = useDebouncedIsLoading(
    [requirementsLoading, loadingSelectQuote, loadingDeselectQuote],
    false
  );

  const getSelectedQuote = useCallback(
    (tariffId: string) => getSelectedQuoteUnwrapped(basket)(tariffId),
    [basket]
  );

  const executeSubmitBroadband = async (fields: BroadbandFormValues) => {
    const selectedQuote = basket?.broadband.quotes.find(
      (quote) => quote.selected
    );
    const newQuote = getSelectedQuote(fields.selectedTariff);
    // Should never happen
    if (!newQuote) {
      throw Error(
        'useBroadbandSubmitter: data inconsistency. New quote not found.'
      );
    }

    const showDigitalHomePhone = shouldShowDigitalHomePhone(newQuote);
    const voipExcluded =
      showDigitalHomePhone && fields.includeVoip === BooleanSelection.No;

    const details: BroadbandUsageRequirementsInput = {
      basketId,
    };

    if (showDigitalHomePhone && fields.includeVoip) {
      details.includeVoip = fields.includeVoip === BooleanSelection.Yes;
    }

    if (fields.wifiUpgrade) {
      details.broadbandEquipment = {
        wholeHomeWifi: {
          numEeros: 2,
        },
      };
    } else {
      details.broadbandEquipment = {
        routerOnly: {
          router: Router.WifiHub,
        },
      };
    }

    if (voipExcluded) {
      details.callingFeatures = null;
      details.lineCharacteristics = { cli: null };
    } else {
      if (fields.callPackage === BooleanSelection.Yes && fields.callPackageId) {
        details.callingFeatures = {
          [fields.callPackageId]: true,
        };
      }
      details.callingFeatures = {
        ...details.callingFeatures,
        exDirectory: true,
      };

      details.lineCharacteristics = {
        cli: fields.landline || undefined,
      };
    }

    if (selectedQuote && selectedQuote.id !== fields.selectedTariff) {
      try {
        await deselectBroadbandQuote({
          variables: {
            input: {
              basketId,
              quoteId: selectedQuote.id,
            },
          },
        });
      } catch (error) {
        return setSnackbarError(error as ErrorLike);
      }
    }

    try {
      await selectBroadbandQuote({
        variables: {
          input: {
            basketId,
            quoteId: fields.selectedTariff,
          },
        },
        onCompleted: (data) =>
          handleSelectedBroadbandItemsForEventTracking(fields, data),
      });
    } catch (error) {
      return setSnackbarError(error as ErrorLike);
    }

    try {
      await submitBroadbandRequirements({ variables: { details } });
    } catch (error) {
      return setSnackbarError(error as ErrorLike);
    }

    if (fields.isOTS) {
      return;
    }

    setModalState(UpdatedTariffModalVariant.productAdded);
    navigate(paths.bundle);
  };

  const handleSubmit = (fields: BroadbandFormValues) =>
    withTrace(SPAN_SUBMIT_BROADBAND, () => executeSubmitBroadband(fields), {
      tariffId: fields.selectedTariff,
      wifiUpgrade: fields.wifiUpgrade,
    });

  const executeRemoveBroadband = async () => {
    if (!basket) return;

    try {
      await Promise.all(
        basket.broadband.quotes
          .filter((quote) => quote.selected)
          .map((quote) =>
            deselectBroadbandQuote({
              variables: {
                input: {
                  basketId: basket.id,
                  quoteId: quote.id,
                },
              },
            })
          )
      );
    } catch {
      return;
    }

    setModalState(UpdatedTariffModalVariant.productRemoved, [
      ProductType.Broadband,
    ]);
    navigate(paths.bundle);
  };

  const removeBroadband = () => {
    if (!basket) return;

    return withTrace(SPAN_REMOVE_BROADBAND, executeRemoveBroadband);
  };

  return {
    handleSubmit,
    isLoading,
    removeBroadband,
    removeBroadbandError: errorDeselectQuote,
  };
};

export { useBroadbandSubmitter };
